import { Link } from 'react-router-dom';
import type { Case } from '../../types';

// Icons
const ExternalLinkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="none" className="inline ml-1">
    <path d="M14 3h3a1 1 0 0 1 1 1v3m-1-4-7.5 7.5M17 3l-7.5 7.5M5 5v10a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9" stroke="#2563eb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface CaseInfoCardProps {
  currentCase: Case;
  caseNumber: string;
  prevCaseNumber: string | null;
  nextCaseNumber: string | null;
  onNavigate: () => void;
}

export function CaseInfoCard({
  currentCase,
  caseNumber,
  prevCaseNumber,
  nextCaseNumber,
  onNavigate,
}: CaseInfoCardProps) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-dark-border" data-onboarding="case-nav">
      {/* Left: Prev arrow */}
      <div className="w-10">
        {prevCaseNumber ? (
          <Link
            to={`/review/${prevCaseNumber}`}
            onClick={onNavigate}
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 dark:bg-dark-hover hover:bg-purple-100 dark:hover:bg-purple-900/30 text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
            title={`Previous: ${prevCaseNumber}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
        ) : (
          <div className="w-8 h-8" />
        )}
      </div>

      {/* Center: Case number + metadata */}
      <div className="flex-1 flex items-center justify-center gap-4" data-onboarding="case-metadata">
        <a
          href={`https://spanside.lightning.force.com/lightning/r/Case/${currentCase.salesforce_id}/view`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-lg font-semibold text-gray-800 dark:text-gray-100 hover:text-purple-600 dark:hover:text-purple-400 transition-colors flex items-center gap-1"
        >
          Case {caseNumber}
          <ExternalLinkIcon />
        </a>
        <span className="text-gray-300 dark:text-gray-600">|</span>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {currentCase.detected_language || 'Unknown language'}
        </span>
        {currentCase.page_count && (
          <>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">{currentCase.page_count} pages</span>
          </>
        )}
      </div>

      {/* Right: Next arrow */}
      <div className="w-10">
        {nextCaseNumber ? (
          <Link
            to={`/review/${nextCaseNumber}`}
            onClick={onNavigate}
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 dark:bg-dark-hover hover:bg-purple-100 dark:hover:bg-purple-900/30 text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
            title={`Next: ${nextCaseNumber}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        ) : (
          <div className="w-8 h-8" />
        )}
      </div>
    </div>
  );
}
